import { useState } from 'react';
import { Check, X } from 'lucide-react';
import type { CheckConstraintModel, TableModel } from '@bits2schema/shared';
import { Modal } from '../../components/Modal';
import { useT } from '../../i18n';
import { defaultCheckConstraintName, describeCheckConstraintSyntaxError, ensureUniqueName } from '../mutations';

interface CheckConstraintDialogProps {
  table: TableModel;
  initial: CheckConstraintModel | null;
  onCancel: () => void;
  onConfirm: (expression: string, name: string) => void;
}

/**
 * Formulář pro vytvoření/editaci check constraintu (E4-US7). Validace výrazu
 * je v MVP jen základní syntaktická kontrola (vyvážené závorky, neprázdný
 * výraz) — epic-4 kap. 8 plný SQL parser vědomě vylučuje z rozsahu.
 */
export function CheckConstraintDialog({ table, initial, onCancel, onConfirm }: CheckConstraintDialogProps) {
  const t = useT();
  const [name, setName] = useState(initial?.name ?? '');
  const [expression, setExpression] = useState(initial?.expression ?? '');

  const syntaxError = describeCheckConstraintSyntaxError(expression);
  const valid = syntaxError === null;

  const confirm = () => {
    if (!valid) return;
    const existingNames = table.checkConstraints
      .filter((candidate) => candidate.id !== initial?.id)
      .map((candidate) => candidate.name);
    const finalName = name.trim() ? ensureUniqueName(name.trim(), existingNames) : defaultCheckConstraintName(table);
    onConfirm(expression.trim(), finalName);
  };

  return (
    <Modal
      title={initial ? t('schema.dialog.check.editTitle') : t('schema.dialog.check.createTitle')}
      onClose={onCancel}
      actions={
        <>
          <button type="button" onClick={onCancel}>
            <X size={16} />
            {t('common.cancel')}
          </button>
          <button type="button" className="primary" disabled={!valid} onClick={confirm}>
            <Check size={16} />
            {initial ? t('schema.dialog.check.editTitle') : t('common.create')}
          </button>
        </>
      }
    >
      <div className="stack">
        <div className="field">
          <label htmlFor="check-name">{t('schema.dialog.check.nameLabel')}</label>
          <input id="check-name" value={name} onChange={(event) => setName(event.target.value)} />
        </div>
        <div className="field">
          <label htmlFor="check-expression">{t('schema.dialog.check.expressionLabel')}</label>
          <textarea
            id="check-expression"
            value={expression}
            onChange={(event) => setExpression(event.target.value)}
            rows={3}
          />
          {syntaxError === 'empty' && <p className="hint">{t('schema.dialog.check.expressionEmpty')}</p>}
          {syntaxError === 'unbalanced' && <p className="hint">{t('schema.dialog.check.expressionUnbalanced')}</p>}
        </div>
      </div>
    </Modal>
  );
}
